import React, { useState, useRef } from 'react';
import { ChevronLeft, Heart, ChevronDown, MessageCircle } from 'lucide-react';
import CatalogProductImage from './CatalogProductImage';

const matchStyle = (compatibility, isWinner) => {
  if (isWinner || compatibility === 'high') return { bg: '#E7F3EA', fg: '#2F7D4F', dot: '#43A86B', label: isWinner ? 'Best Match' : 'High Match' };
  if (compatibility === 'medium') return { bg: '#FBF0DA', fg: '#A8742F', dot: '#D7A933', label: 'Medium Match' };
  return { bg: '#F0EEF0', fg: '#6B6B70', dot: '#A6A6AC', label: 'Low Match' };
};

const firstSentence = (text) => {
  const raw = (text || '').trim();
  if (!raw) return '';
  const m = raw.split(/(?<=[.!?])\s+(?=[A-Z])/);
  return m[0] || raw;
};

const KEY_INGREDIENT_PREVIEW = 6;

const ProductCard = ({ product, details, isWinner, onSave }) => {
  const [saved, setSaved] = useState(false);
  const [open, setOpen] = useState(true);  // formulation open by default (Sofia)
  const [ingExpanded, setIngExpanded] = useState(false);

  const ing = Array.isArray(details.key_ingredients)
    ? details.key_ingredients
    : String(details.key_ingredients || '').split(',').map(s => s.trim()).filter(Boolean);
  const formula = details.formulation_bullets || [];
  const formulaVerified = details.formula_status === 'verified';
  const ms = matchStyle(details.compatibility, isWinner);
  const verdict = firstSentence(details.reasoning);
  const pros = details.pros || [];
  const cons = details.cons || [];

  const ingVisible = ingExpanded ? ing : ing.slice(0, KEY_INGREDIENT_PREVIEW);
  const ingExtra = ing.length - KEY_INGREDIENT_PREVIEW;

  return (
    <div
      // 2026-06-09 (Sofia "Instagram carousel"): each slide is its OWN vertical
      // scroll container (h-full overflow-y-auto). The outer carousel scrolls
      // horizontal-only with mandatory snap. This separates the axes cleanly:
      // vertical scroll stays inside a slide, horizontal swipe moves between
      // slides and ALWAYS lands fully on one (scrollSnapStop: always) — never
      // resting mid-swipe.
      className="flex-shrink-0 w-full h-full overflow-y-auto px-4"
      style={{ scrollSnapAlign: 'start', scrollSnapStop: 'always' }}
    >
      {/* 2026-06-09: borderless / shadowless treatment per Sofia feedback.
          Outer card frame was making the content feel boxed-in; sections now
          read as page content within the carousel slot. Carousel itself is
          preserved (you still swipe between products). */}
      <div className="bg-white overflow-hidden">
        {/* Header */}
        <div className="px-2 pt-2 pb-4 flex gap-3.5 items-start">
          <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 bg-[#F3D5B5]/20 flex items-center justify-center">
            <CatalogProductImage src={product.image} alt={product.name} brand={product.brand} className="w-full h-full object-cover" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-[#2A324B] text-[15px] leading-snug">{product.name}</h3>
            <p className="text-xs text-[#2A324B]/55 mb-1.5">{product.brand}</p>
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[13px] font-semibold"
              style={{ background: ms.bg, color: ms.fg }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: ms.dot }} />
              {ms.label}
            </span>
          </div>
          <button
            type="button"
            onClick={() => { setSaved(s => !s); onSave && onSave(); }}
            className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-colors"
            style={{ background: saved ? '#2A324B' : 'rgba(42,50,75,0.06)' }}
            aria-label={saved ? 'Saved' : 'Save'}
          >
            <Heart className="w-4 h-4" style={{ color: saved ? '#fff' : '#2A324B' }} fill={saved ? '#fff' : 'none'} />
          </button>
        </div>

        {/* Verdict — the one-line why */}
        {verdict && (
          <div className="px-2 pb-5">
            <p className="text-[15px] leading-relaxed text-[#2A324B] font-medium">{verdict}</p>
          </div>
        )}

        <div className="px-2 pb-4">
          <p className="text-[12px] leading-relaxed text-[#2A324B]/55">
            {formulaVerified
              ? 'Formula verified against Son:mi’s product data.'
              : 'Formula not verified. This comparison uses the product type and your context; no ingredient claims are included.'}
          </p>
        </div>

        {/* Key ingredients — collapsible past the first 6 so long INCI lists
            don't dominate the screen. */}
        {ing.length > 0 && (
          <div className="px-2 pb-5">
            <p className="text-[13px] font-semibold tracking-[0.16em] uppercase text-[#2A324B]/40 mb-2">Key Ingredients</p>
            <div className="flex flex-wrap gap-1.5">
              {ingVisible.map((x, i) => (
                <span key={i} className="text-xs text-[#2A324B] px-2.5 py-1 rounded-full bg-white border" style={{ borderColor: 'rgba(42,50,75,0.15)' }}>
                  {x}
                </span>
              ))}
              {!ingExpanded && ingExtra > 0 && (
                <button
                  type="button"
                  onClick={() => setIngExpanded(true)}
                  className="text-xs px-2.5 py-1 rounded-full"
                  style={{ color: 'rgba(42,50,75,0.65)', background: 'rgba(42,50,75,0.06)' }}
                >
                  +{ingExtra} more
                </button>
              )}
            </div>
          </div>
        )}

        {/* Understanding the formulation — moved right under the ingredients
            (Sofia) and open by default. The INCI-level breakdown: where each
            ingredient sits + what it does in this formula. */}
        {formula.length > 0 && (
          <div className="px-2">
            <button
              type="button"
              onClick={() => setOpen(o => !o)}
              className="w-full flex items-center justify-between py-3.5 text-[15px] font-semibold text-[#2A324B]/85 hover:text-[#2A324B]"
              style={{ borderTop: '1px solid rgba(52,28,28,0.08)' }}
            >
              {open ? 'Hide details' : 'Understanding the formulation'}
              <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: 'rgba(42,50,75,0.45)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
            </button>
            {open && (
              <ul className="pb-5 pt-1 space-y-2">
                {formula.map((b, i) => (
                  <li key={i} className="text-[15px] text-[#2A324B]/80 flex gap-2 leading-relaxed">
                    <span className="text-[#C8956A] mt-0.5 flex-shrink-0">•</span>
                    <span><span className="font-semibold text-[#2A324B]">{b.ingredient}</span>{b.note ? ` — ${b.note}` : ''}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* What this means for you — labelled header (Sofia) so it reads as a
            distinct section from the formulation above, matching Product Analysis. */}
        {(pros.length > 0 || cons.length > 0) && (
          <div className="px-2 pb-2">
            <h4 className="text-[15px] font-semibold text-[#2A324B] mb-3">What this means for you</h4>
            <div className="space-y-2.5">
              {pros.slice(0, 2).map((p, i) => (
                <div key={`p${i}`} className="flex items-start gap-3">
                  <span className="mt-0.5 w-4 h-4 rounded-full border-2 border-green-400 flex items-center justify-center flex-shrink-0">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                  </span>
                  <span className="text-[15px] text-[#2A324B]/80 leading-snug">{p}</span>
                </div>
              ))}
              {cons.slice(0, 1).map((c, i) => (
                <div key={`c${i}`} className="flex items-start gap-3">
                  <span className="mt-0.5 w-4 h-4 rounded-full border-2 border-amber-400 flex items-center justify-center flex-shrink-0">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                  </span>
                  <span className="text-[15px] text-[#2A324B]/80 leading-snug">{c}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ComparisonResultsRedesign = ({ comparisonResult, products, onBack, onCompareAgain, saveProduct, onAsk }) => {
  const list = products || comparisonResult.products || [];
  const winnerId = comparisonResult?.overall?.winner;
  const scrollRef = useRef(null);
  const [active, setActive] = useState(0);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const idx = Math.round(el.scrollLeft / el.clientWidth);
    if (idx !== active) setActive(idx);
  };

  const goTo = (i) => {
    setActive(i);
    const el = scrollRef.current;
    if (el) el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' });
  };

  return (
    <div className="flex flex-col h-full" style={{ background: '#FFFFFF' }}>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 pt-3 pb-2 flex-shrink-0">
        <button onClick={onBack} className="p-1.5 rounded-xl hover:bg-black/5">
          <ChevronLeft className="h-5 w-5 text-[#2A324B]" />
        </button>
        <div>
          <h2 className="text-lg font-bold text-[#2A324B] leading-tight">Comparison Results</h2>
          <p className="text-xs text-[#2A324B]/45">Swipe to compare · {list.length} products</p>
        </div>
      </div>

      {/* Tabs / dots */}
      <div className="flex items-center justify-center gap-2 px-4 pb-3 flex-shrink-0">
        {list.map((p, i) => {
          const isW = (p.id || p.product_id) === winnerId;
          return (
            <button
              key={i}
              onClick={() => goTo(i)}
              className="px-3 py-1.5 rounded-full text-xs font-semibold transition-all"
              style={{
                background: active === i ? '#2A324B' : 'rgba(42,50,75,0.07)',
                color: active === i ? '#fff' : 'rgba(42,50,75,0.6)',
              }}
            >
              {isW ? '★ ' : ''}{(p.name || `Product ${i + 1}`).split(' ').slice(0, 2).join(' ')}
            </button>
          );
        })}
      </div>

      {/* Swipe carousel.
          2026-06-09: previous version had `touch-action: pan-y pinch-zoom`
          which killed horizontal swipe entirely — vertical reads worked but
          you couldn't swipe between products anymore (Sofia's batch-2 feedback).
          Correct fix is `touch-action: pan-x pan-y` so BOTH axes work and the
          browser decides which based on swipe vector. Snap stays at `proximity`
          (not `mandatory`) so it's advisory and won't yank a near-vertical
          swipe into a hard page-change. Overscroll-behavior-x: contain still
          blocks accidental back-navigation gestures. */}
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="flex-1 flex overflow-x-auto overflow-y-hidden items-stretch"
        style={{
          // mandatory (was proximity) so a horizontal swipe always completes to
          // a full page — fixes the "stuck mid-swipe / rests between pages"
          // complaint. overflow-y-hidden here because each slide now owns its
          // own vertical scroll (see ProductCard) — keeps the two axes from
          // fighting. items-stretch so each slide fills the carousel height.
          scrollSnapType: 'x mandatory',
          scrollbarWidth: 'none',
          WebkitOverflowScrolling: 'touch',
          touchAction: 'pan-x pan-y',
          overscrollBehaviorX: 'contain',
        }}
      >
        {list.map((product, i) => {
          const details = comparisonResult.details[product.id] || comparisonResult.details[product.product_id] || {};
          const isWinner = (product.id || product.product_id) === winnerId;
          return (
            <ProductCard
              key={product.id || product.product_id || i}
              product={product}
              details={details}
              isWinner={isWinner}
              onSave={() => saveProduct && saveProduct(product)}
            />
          );
        })}
      </div>

      {/* Bottom actions */}
      <div className="flex gap-3 px-4 py-4 flex-shrink-0" style={{ borderTop: '1px solid rgba(52,28,28,0.06)' }}>
        <button
          onClick={onCompareAgain}
          className="flex-1 py-3 rounded-full text-sm font-semibold text-[#2A324B] transition-colors"
          style={{ border: '1.5px solid rgba(42,50,75,0.18)', background: '#fff' }}
        >
          Compare others
        </button>
        <button
          onClick={onAsk}
          className="flex-1 py-3 rounded-full text-sm font-semibold text-white transition-colors flex items-center justify-center gap-1.5"
          style={{ background: '#2A324B' }}
        >
          <MessageCircle className="w-4 h-4" /> Ask a question
        </button>
      </div>
    </div>
  );
};

export default ComparisonResultsRedesign;
